/**
 * Gk2ControlConfigEditor — per-org config of the gk2control presence/clock-in
 * integration. The API token is write-only (shown as "configurado"). The
 * webhookSecret is generated server-side on first enable and shown ONCE in the
 * response so the admin can paste it into gk2control; afterwards we only know
 * that it is set. The "Probar conexión" button hits the org's gk2control with
 * the saved token to verify it works.
 *
 * Auto-salida settings (P6) are not exposed here yet; they will appear once
 * the scheduled-job that fires the automatic clock-out at shift end is wired.
 */
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import toast from 'react-hot-toast';
import { organizationsApi } from '../../services/api';
import { LoadingSpinner } from '../common';

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';

function Toggle({ on, onClick, disabled }: { on: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-vida-600' : 'bg-gray-200'} ${disabled ? 'opacity-50' : ''}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

function publicApiBase(): string {
  // Dashboard and API share the same public origin via reverse proxy in this
  // deployment, so building the URL against window.location works reliably.
  // We deliberately avoid reading the Vite injected variable at runtime: the
  // bracketed form is undefined in production builds and would crash the
  // whole modal as soon as this function executes.
  return `${window.location.origin}/api`;
}

export interface Gk2ControlConfigEditorHandle {
  save: () => Promise<boolean>;
  isSaving: () => boolean;
}

const Gk2ControlConfigEditor = forwardRef<Gk2ControlConfigEditorHandle, { orgId: string; slug?: string }>(function Gk2ControlConfigEditor({ orgId, slug }, ref) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [companyNif, setCompanyNif] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('https://gk2control.com');
  const [apiToken, setApiToken] = useState('');
  const [apiTokenSet, setApiTokenSet] = useState(false);
  const [webhookSecretSet, setWebhookSecretSet] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [autoSalidaEnabled, setAutoSalidaEnabled] = useState(false);
  const [autoSalidaGraceMin, setAutoSalidaGraceMin] = useState(0);
  const [acceptUnsignedWebhook, setAcceptUnsignedWebhook] = useState(false);
  // Redondeo de fichajes (A1) — OPT-IN.
  const [roundingEnabled, setRoundingEnabled] = useState(false);
  const [roundingIntervalMin, setRoundingIntervalMin] = useState(0);
  const [roundingMarginMin, setRoundingMarginMin] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    organizationsApi.getGk2ControlConfig(orgId)
      .then((r) => {
        if (cancelled) return;
        const d = r.data?.data || {};
        setEnabled(!!d.enabled);
        setCompanyNif(d.companyNif || '');
        setApiBaseUrl(d.apiBaseUrl || 'https://gk2control.com');
        setApiTokenSet(!!d.apiTokenSet);
        setWebhookSecretSet(!!d.webhookSecretSet);
        setAutoSalidaEnabled(!!d.autoSalidaEnabled);
        setAutoSalidaGraceMin(typeof d.autoSalidaGraceMin === 'number' ? d.autoSalidaGraceMin : 0);
        setAcceptUnsignedWebhook(!!d.acceptUnsignedWebhook);
        setRoundingEnabled(!!d.roundingEnabled);
        setRoundingIntervalMin(typeof d.roundingIntervalMin === 'number' ? d.roundingIntervalMin : 0);
        setRoundingMarginMin(typeof d.roundingMarginMin === 'number' ? d.roundingMarginMin : 0);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [orgId]);

  const save = async (): Promise<boolean> => {
    setSaving(true);
    try {
      const r = await organizationsApi.saveGk2ControlConfig(orgId, {
        enabled,
        companyNif: companyNif.trim(),
        apiBaseUrl: apiBaseUrl.trim() || 'https://gk2control.com',
        autoSalidaEnabled,
        autoSalidaGraceMin: Math.max(0, Math.min(120, Number(autoSalidaGraceMin) || 0)),
        acceptUnsignedWebhook,
        roundingEnabled,
        roundingIntervalMin: Math.max(0, Math.min(120, Number(roundingIntervalMin) || 0)),
        roundingMarginMin: Math.max(0, Math.min(60, Number(roundingMarginMin) || 0)),
        ...(apiToken.trim() ? { apiToken: apiToken.trim() } : {}),
      });
      if (apiToken.trim()) { setApiTokenSet(true); setApiToken(''); }
      const secret = (r.data as { webhookSecret?: string })?.webhookSecret;
      if (secret) {
        setRevealedSecret(secret);
        setWebhookSecretSet(true);
      }
      toast.success('Configuración guardada');
      return true;
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save, isSaving: () => saving }), [save, saving]);

  const regenerateSecret = async () => {
    if (!confirm('Se generará un nuevo secreto del webhook. El anterior dejará de ser válido. ¿Continuar?')) return;
    try {
      const r = await organizationsApi.regenerateGk2WebhookSecret(orgId);
      const secret = (r.data as { webhookSecret?: string })?.webhookSecret;
      if (secret) {
        setRevealedSecret(secret);
        setWebhookSecretSet(true);
        toast.success('Nuevo secreto generado');
      }
    } catch {
      toast.error('Error al regenerar el secreto');
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      // Send the inline values so the test works BEFORE saving (first-time
      // setup). Backend falls back to the saved token/baseUrl if either is empty.
      const r = await organizationsApi.testGk2ControlConnection(orgId, {
        ...(apiToken.trim() ? { apiToken: apiToken.trim() } : {}),
        ...(apiBaseUrl.trim() ? { apiBaseUrl: apiBaseUrl.trim() } : {}),
      });
      const d = r.data as { ok?: boolean; status?: number; message?: string; error?: string };
      if (d.ok) toast.success('Conexión correcta');
      else toast.error(`Conexión fallida${d.status ? ` (${d.status})` : ''}: ${d.message || d.error || 'sin detalle'}`);
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al probar la conexión');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-4 text-gray-400"><LoadingSpinner size="sm" /></div>;
  }

  const webhookUrl = slug ? `${publicApiBase()}/public/gk2control/webhook/${slug}` : '';

  return (
    <div className="space-y-4">
      <label className="label">Control de presencia (gk2control)</label>

      <div className="rounded-lg border border-gray-200 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">Activo</span>
          <Toggle on={enabled} onClick={() => setEnabled((v) => !v)} disabled={saving} />
        </div>

        {enabled && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">NIF de la empresa en gk2control</label>
                <input className={inputClass} value={companyNif} onChange={(e) => setCompanyNif(e.target.value)} placeholder="B58087487" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">URL base de la API</label>
                <input className={inputClass} value={apiBaseUrl} onChange={(e) => setApiBaseUrl(e.target.value)} placeholder="https://gk2control.com" />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Token de API {apiTokenSet && <span className="text-green-600">(configurado)</span>}
              </label>
              <input type="password" className={inputClass} value={apiToken} onChange={(e) => setApiToken(e.target.value)}
                placeholder={apiTokenSet ? '•••••• (dejar vacío para mantener)' : 'Pega aquí el token generado en gk2control'} autoComplete="off" />
              <p className="text-xs text-gray-400 mt-1">
                Se genera en gk2control (acceso como administrador → API/Webhooks). Se guarda cifrado y nunca se vuelve a mostrar.
              </p>
            </div>

            {/* Webhook (entrante) */}
            <div className="rounded-lg border border-gray-200 p-2.5 space-y-2 bg-gray-50/50">
              <p className="text-xs font-medium text-gray-700">Webhook entrante (gk2control → este sistema)</p>
              {slug ? (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">URL del webhook</label>
                  <div className="flex gap-2">
                    <input readOnly value={webhookUrl}
                      className={`${inputClass} font-mono text-xs bg-gray-50`} onFocus={(e) => e.currentTarget.select()} />
                    <button type="button"
                      onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('URL copiada'); }}
                      className="shrink-0 px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">Copiar</button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400">El identificador de la organización (slug) no está disponible.</p>
              )}

              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Secreto del webhook {webhookSecretSet && !revealedSecret && <span className="text-green-600">(configurado)</span>}
                </label>
                {revealedSecret ? (
                  <div className="space-y-1">
                    <div className="flex gap-2">
                      <input readOnly value={revealedSecret}
                        className={`${inputClass} font-mono text-xs bg-yellow-50 border-yellow-300`} onFocus={(e) => e.currentTarget.select()} />
                      <button type="button"
                        onClick={() => { navigator.clipboard.writeText(revealedSecret); toast.success('Secreto copiado'); }}
                        className="shrink-0 px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">Copiar</button>
                    </div>
                    <p className="text-xs text-amber-700">
                      Cópialo ahora: por seguridad no se volverá a mostrar. Pégalo en gk2control para firmar los webhooks.
                    </p>
                  </div>
                ) : (
                  <button type="button" onClick={regenerateSecret}
                    className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                    {webhookSecretSet ? 'Regenerar secreto' : 'Generar secreto'}
                  </button>
                )}
              </div>

              {/* Fallback for providers (like gk2control) that don't expose a
                  secret field on their side. The receiver also accepts the
                  saved API token as Bearer in 'Authorization' automatically;
                  this toggle is the last-resort opt-in when there is no auth
                  at all on the incoming webhook. */}
              <div className="flex items-center justify-between border-t border-gray-200 pt-2 mt-2">
                <span className="text-xs font-medium text-gray-700">Aceptar webhooks sin firma</span>
                <Toggle on={acceptUnsignedWebhook} onClick={() => setAcceptUnsignedWebhook((v) => !v)} disabled={saving} />
              </div>
              <p className="text-xs text-gray-400">
                Activa esto si <strong>gk2control no permite configurar un secreto</strong> y tampoco te envía el token de API en una cabecera <code>Authorization</code>. Con el toggle activo cualquier petición POST a la URL del webhook entrará: la URL incluye el slug de la org y debes considerarla sensible. Hay rate-limit (600 req/min por IP).
              </p>
            </div>

            {/* Auto-clock-out (P6) */}
            <div className="rounded-lg border border-gray-200 p-2.5 space-y-2 bg-gray-50/50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Fichaje automático de salida al fin del turno</span>
                <Toggle on={autoSalidaEnabled} onClick={() => setAutoSalidaEnabled((v) => !v)} disabled={saving} />
              </div>
              {autoSalidaEnabled && (
                <div className="grid grid-cols-2 gap-2 items-end">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Margen de gracia (minutos)</label>
                    <input type="number" min={0} max={120} className={inputClass}
                      value={autoSalidaGraceMin}
                      onChange={(e) => setAutoSalidaGraceMin(Number(e.target.value))} />
                  </div>
                  <p className="text-xs text-gray-500 leading-snug">
                    Tras superarse el fin del turno planificado en este margen, el sistema cierra la salida si hay entrada del día sin salida posterior.
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-400">
                Solo se cierra la salida; nunca se ficha la entrada. Si el empleado fichó antes, no se hace nada. Si nunca entró, no se hace nada.
              </p>
            </div>

            {/* Redondeo de fichajes (A1) — OPT-IN */}
            <div className="rounded-lg border border-gray-200 p-2.5 space-y-2 bg-gray-50/50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Redondear fichajes en el cómputo</span>
                <Toggle on={roundingEnabled} onClick={() => setRoundingEnabled((v) => !v)} disabled={saving} />
              </div>
              {roundingEnabled && (
                <div className="grid grid-cols-2 gap-2 items-end">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Intervalo (minutos)</label>
                    <input type="number" min={0} max={120} className={inputClass}
                      value={roundingIntervalMin}
                      onChange={(e) => setRoundingIntervalMin(Number(e.target.value))} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Margen de gracia (minutos)</label>
                    <input type="number" min={0} max={60} className={inputClass}
                      value={roundingMarginMin}
                      onChange={(e) => setRoundingMarginMin(Number(e.target.value))} />
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400">
                Solo afecta al <strong>cómputo</strong> (parte de horas y salida forzosa); el fichaje real se guarda sin tocar. Entrada: sube al siguiente límite salvo que caiga dentro del margen tras uno (p.ej. intervalo 30 / margen 5: 07:40→08:00, 07:15→07:30, 08:02→08:00). Salida: baja al límite salvo que caiga dentro del margen antes del siguiente.
              </p>
            </div>

            <p className="text-xs text-gray-400">
              Esta integración importa los fichajes de gk2control (en tiempo real vía webhook y/o por sincronización manual) y permite ver, por recurso, la comparativa entre lo planificado y lo realmente fichado.
            </p>
          </>
        )}
      </div>

      {/* Acciones al pie del editor: dejar los botones al final para que sea
          evidente que TODA la sección de arriba (incluidos webhook y auto-
          salida) se guarda. Antes estaban en la cabecera y daba la sensación
          de que los toggles inferiores no se persistían. */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button type="button" onClick={testConnection} disabled={testing || saving || !(apiTokenSet || apiToken.trim())}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
          title={(apiTokenSet || apiToken.trim()) ? 'Llama a GET /records?limit=1 con el token actual' : 'Introduce o guarda primero el token'}>
          {testing && <LoadingSpinner size="sm" />} Probar conexión
        </button>
        {saving && <span className="text-xs text-gray-500 inline-flex items-center gap-1"><LoadingSpinner size="sm" /> Guardando…</span>}
      </div>
    </div>
  );
});

export default Gk2ControlConfigEditor;
